import React, { useEffect, useState } from 'react';
import { X, Clock, User, MessageSquare, ArrowRight } from 'lucide-react';
import api from './axiosConfig';
import { format } from 'date-fns'; 
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة

const StatusHistoryModal = ({ appName, recordId, onClose }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await api.get(`/status/history/${appName}/${recordId}`);
        setHistory(res.data);
      } catch (err) {
        console.error("Failed to load history");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [appName, recordId]);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-surface-card rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[80vh]">

        {/* Header */}
        <div className="bg-primary-dark p-4 flex justify-between items-center text-ink-inverse">
          <div className="flex items-center gap-3">
            <Clock size={20} className="text-ink-inverse/70" />
            <div>
              <h3 className="font-black uppercase tracking-tighter text-sm">{t('statusHistory.title')}</h3>
              <p className="text-[10px] text-ink-inverse/70">{appName}: {recordId}</p>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-ink-inverse/20 p-1 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-surface-panel custom-scrollbar">
          {loading ? (
            <div className="flex justify-center p-10 font-bold text-ink-secondary animate-pulse">{t('statusHistory.loading')}</div>
          ) : history.length === 0 ? (
            <div className="text-center p-20 text-ink-secondary italic">{t('statusHistory.noRecords')}</div>
          ) : (
            <div className="space-y-4">
              {history.map((item, index) => (
                <div key={index} className="bg-surface-card border border-line rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
                  {/* Decorative Timeline line */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary"></div>

                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Status Change */}
                    <div className="flex items-center gap-3">
                      <span className="px-2 py-1 bg-surface-panel text-ink-secondary rounded text-[10px] font-bold border border-line uppercase">
                        {item.old_status || t('statusHistory.initial')}
                      </span>
                      <ArrowRight size={14} className={`text-ink-secondary ${isRTL ? 'rotate-180' : ''}`} />
                      <span className="px-2 py-1 bg-primary-soft text-primary rounded text-[10px] font-bold border border-primary/20 uppercase">
                        {item.new_status}
                      </span>
                    </div>

                    {/* Metadata */}
                    <div className="flex items-center gap-4 text-[11px] text-ink-secondary font-medium">
                      <div className="flex items-center gap-1.5">
                        <User size={14} className="text-primary" />
                        <span className="font-bold text-ink">{item.changeby}</span>
                      </div>
                      <div className="flex items-center gap-1.5 border-l border-line pl-4">
                        <Clock size={14} className="text-accent" />
                        <span>{format(new Date(item.changedate), 'yyyy-MM-dd HH:mm')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Memo/Reason */}
                  {item.memo && (
                    <div className="mt-3 bg-surface-panel p-2 rounded border-l-2 border-accent/30 flex items-start gap-2 italic text-[11px] text-ink-secondary">
                      <MessageSquare size={12} className="mt-0.5 text-ink-secondary shrink-0" />
                      <span>{item.memo}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-surface-panel p-3 flex justify-end border-t border-line">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-primary-dark text-ink-inverse rounded font-bold uppercase text-[10px] hover:bg-primary transition-all"
          >
            {t('statusHistory.btnClose')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusHistoryModal;